import React, { useEffect, useState } from 'react';
import { setHeaderTitle } from 'features/ui/model/uiSlice';
import { useAppDispatch } from 'app/store';
import { useParams } from 'react-router-dom';
import { Box, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material';
import { StyledCard } from 'shared/ui/Card/StyledCard';
import { MoreInfo } from 'shared/ui/MoreInfo/MoreInfo';
import {
  useGetAdminAwardsQuery,
  useGetAdminCompetitionItemQuery,
  updateCompetitionStatusThunk,
} from 'entities/competitions-b2b';
import { retrieveStatus } from 'shared/helpers/retriveStatus';
import { useGetMechanicDetailQuery } from 'widgets/mechanicDetailPage/api/getMechanicDetailApi';
import { toast } from 'react-toastify';
import ParticipantsXlsxLoader from 'widgets/competitions-b2b/ui/ParticipantsXlsxLoader';
import { CustomTabPanel } from 'entities/table/ui/CustomTabPanel';
import { a11yProps } from 'entities/table/ui/model';
import DrawerCompetition from '../ui/DrawerCompetition';
import { CompetitionStatsWidget } from '../stats';
import { CompetitionReportsWidget } from '../reports';

enum Status {
  Draft = 1,
  Planned = 2,
  Active = 3,
  Finished = 4,
  Canceled = 5,
}

export const CompetitionsDetail = () => {
  const params = useParams();
  const id = parseInt(params.id || '');

  const dispatch = useAppDispatch();
  const { data: competitionData, refetch: refetchCompetitionData } = useGetAdminCompetitionItemQuery({
    competition_id: id,
  });
  const { data: mechanicDetail } = useGetMechanicDetailQuery({
    app_type: 'admin',
    id: competitionData?.objective_id || 0,
  });
  const { data: awards, refetch: refetchAwards } = useGetAdminAwardsQuery({
    competition_id: id,
  });
  const [status, setStatus] = useState<Status>(competitionData?.status || 1);
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getDate = (value: string | undefined) => value && new Date(value).toLocaleDateString('ru');
  const data = {
    id,
    name: competitionData?.name,
    status: competitionData ? retrieveStatus(competitionData.status) : Status.Draft,
    date_award_end: getDate(competitionData?.date_award_end),
    date_award_start: getDate(competitionData?.date_award_start),
    date_end: getDate(competitionData?.date_end),
    date_start: getDate(competitionData?.date_start),
    participants_count: competitionData?.participants_count,
    date_current_rating: getDate(competitionData?.date_current_rating),
  };

  useEffect(() => {
    dispatch(setHeaderTitle(competitionData?.name || 'Соревнование'));

    if (competitionData?.status) {
      setStatus(competitionData?.status as Status);
    }
  }, [dispatch, competitionData]);

  const handleAddAwards = (awardsData?: any[]) => {
    const awardsToCheck = awardsData || awards;
    if (!awardsToCheck || awardsToCheck.length === 0) {
      toast.error('Добавьте в соревнование призовые места!');
      return false;
    }
    return true;
  };

  const handleMinimumAwards = (awardsData?: any[]) => {
    const awardsToCheck = awardsData || awards;
    if (awardsToCheck && awardsToCheck.length < 3) {
      toast.error('Соревнование обязательно должно иметь 1, 2 и 3 призовые места');
      return false;
    }
    return true;
  };

  /**
   * @description Обработчик изменения статуса
   * @param newStatus
   */
  const handleOnChangeStatus = async (newStatus: Status) => {
    const oldStatus = status;
    const awardsResult = await refetchAwards();
    await refetchCompetitionData();
    if (!competitionData || !id || newStatus === competitionData?.status) return;
    if (oldStatus === Status.Draft) {
      if (!handleAddAwards(awardsResult.data) || !handleMinimumAwards(awardsResult.data)) {
        return;
      }
    }
    dispatch(
      updateCompetitionStatusThunk({
        competition_id: id,
        status: newStatus,
      }),
    ).then((res) => {
      if (res.payload && (res.payload as { status: number }).status > 400) {
        setStatus(oldStatus);
      } else {
        setStatus(newStatus);
      }
    });
  };

  return (
    <>
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Tab
            label="Основная информация"
            {...a11yProps(0)}
          />
          <Tab
            label="Статистика"
            {...a11yProps(1)}
          />
        </Tabs>
        {competitionData ? (
          <CompetitionReportsWidget
            id={id}
            title={`Отчет ${competitionData.name}`}
          />
        ) : null}
      </Box>

      <CustomTabPanel
        index={0}
        value={value}
      >
        <Box
          component={StyledCard}
          sx={{
            marginBottom: '24px',
          }}
        >
          <Box
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography>Детали соревнования</Typography>
            <Box
              style={{
                width: '30%',
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="select-label-type">Статус</InputLabel>
                <Select
                  labelId="select-label-type"
                  label="Статус"
                  sx={{ marginBottom: '24px', width: '100%' }}
                  value={status}
                  onChange={(e) => handleOnChangeStatus(e.target.value as Status)}
                >
                  <MenuItem value={1}>Черновик</MenuItem>
                  <MenuItem value={2}>Запланированное</MenuItem>
                  <MenuItem value={3}>Активное</MenuItem>
                  <MenuItem value={4}>Завершенное</MenuItem>
                  <MenuItem value={5}>Отмененное</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          {competitionData?.image_main ? (
            <img
              src={competitionData?.image_main}
              alt=""
              style={{ width: 64, height: 64 }}
            />
          ) : null}
          <Box
            style={{
              display: 'flex',
              marginTop: '12px',
              width: '100%',
            }}
          >
            <div
              className="column"
              style={{ width: '600px' }}
            >
              <MoreInfo>
                <div className="title">ID</div>
                <div className="content">{data?.id ?? '---'}</div>
              </MoreInfo>
              <MoreInfo>
                <div className="title">Статус</div>
                <div className="content">{data?.status ?? '---'}</div>
              </MoreInfo>
              {/* <MoreInfo>
                                <div className="title">Число участников</div>
                                <div className="content">{data?.participants_count ?? "0"}</div>
                            </MoreInfo> */}
              <MoreInfo>
                <div className="title">Число призовых мест</div>
                <div className="content">{awards?.length ?? '0'}</div>
              </MoreInfo>
              <MoreInfo>
                <div className="title">Механика</div>
                <div className="content">{mechanicDetail?.name ?? '---'}</div>
              </MoreInfo>
            </div>
            <div
              className="column"
              style={{ width: '600px' }}
            >
              <MoreInfo>
                <div className="title">Период проведения соревнования</div>
                <div className="content">
                  {data?.date_start ?? '---'} - {data?.date_end ?? '---'}
                </div>
              </MoreInfo>
              <MoreInfo>
                <div className="title">Период выдачи призов</div>
                <div className="content">
                  {data?.date_award_start ?? '---'} - {data?.date_award_end ?? '---'}
                </div>
              </MoreInfo>
              <MoreInfo>
                <div className="title">Дата текущего рейтинга</div>
                <div className="content">{data?.date_current_rating ?? '---'}</div>
              </MoreInfo>
            </div>
          </Box>
          <div style={{ width: '100%' }}>
            <MoreInfo>
              <div className="title">Краткое описание</div>
              <div className="content">{competitionData?.description_short ?? '---'}</div>
            </MoreInfo>
            <div style={{ width: '100%' }}>
              <div className="title">Описание</div>
              <div className="content">{competitionData?.description ?? '---'}</div>
            </div>
          </div>
        </Box>
        <Box component={StyledCard}>
          <ParticipantsXlsxLoader
            participants={data?.participants_count || 0}
            competitionId={id}
          />
        </Box>
      </CustomTabPanel>

      <CustomTabPanel
        index={1}
        value={value}
      >
        {competitionData?.participants_count ? (
          <CompetitionStatsWidget
            dateAwardEnd={competitionData.date_award_end}
            id={id}
            participantsCount={competitionData.participants_count}
          />
        ) : (
          <Box>
            <Typography sx={{ mt: 3 }}>Статистика не сформирована</Typography>
          </Box>
        )}
      </CustomTabPanel>

      <DrawerCompetition competitionId={id} />
    </>
  );
};
